// Issue d'un appel : effets purs sur la fiche et sur le RDV. Module pur (worker et front).

import { addDaysIso, parisDateOf } from "./followup";
import type { ProspectStatus, RdvIssue, RdvOutcome } from "./schema";

export const OUTCOME_CHOICES = [
  "closed_pc",
  "closed_acc",
  "relance",
  "lost",
  "no_show",
  "present",
] as const;
export type OutcomeChoice = (typeof OUTCOME_CHOICES)[number];

export interface OutcomeOpts {
  /** RDV concerné ; sans lui, aucune issue n'est écrite sur un RDV. */
  eventId?: number;
  /** YYYY-MM-DD, obligatoire pour « relance » ; facultatif pour « no_show » (défaut : aujourd'hui). */
  followUpAt?: string;
  /** HH:mm (heure de Paris), facultatif pour « relance » : gardée dans la prochaine action, la date reste un jour. */
  followUpTime?: string;
  blocker?: string;
  note?: string;
  lossReason?: string;
  /** Faux : le RDV pointé n'est pas le dernier récent de la fiche, la fiche reste intacte (défaut vrai). */
  touchesProspect?: boolean;
  /** La fiche a déjà une relance à venir : « présent » ne pose pas de relance de sécurité (défaut faux). */
  hasUpcomingFollowUp?: boolean;
  /** Prochaine action actuelle de la fiche : « Rappeler » est remplacée par la relance de sécurité d'un « présent ». */
  currentNextAction?: string | null;
  /** YYYY-MM-DD, jour de Paris du RDV pointé ; sert au résumé quand la fiche n'est pas touchée. */
  eventDay?: string;
}

export interface OutcomeEffects {
  status?: ProspectStatus;
  nextFollowUpAt?: string | null;
  nextAction?: string | null;
  lossReason?: string | null;
  eventOutcome?: RdvOutcome;
  /** Issue notée sur le RDV ; null l'efface (absent) ; absent du résultat : on n'y touche pas (présent). */
  eventIssue?: RdvIssue | null;
}

/** « 14:00 » → « 14h », « 14:30 » → « 14h30 ». */
export function hourLabel(time: string): string {
  return time.replace(":00", "h").replace(":", "h");
}

function clean(s: string | undefined): string | null {
  const t = s?.trim();
  return t ? t : null;
}

/**
 * Table « Issue » de la spec. Les ventes (closed_pc / closed_acc) sont créées avant l'appel de
 * cette fonction : le statut n'est donc jamais touché ici.
 */
export function outcomeEffects(
  choice: OutcomeChoice,
  today: string,
  opts: OutcomeOpts = {},
): OutcomeEffects {
  const hasEvent = opts.eventId !== undefined;
  switch (choice) {
    case "closed_pc":
    case "closed_acc":
      return {
        nextFollowUpAt: null,
        nextAction: null,
        ...(hasEvent ? { eventOutcome: "attended" as const, eventIssue: "closed" as const } : {}),
      };
    case "relance": {
      if (!opts.followUpAt) throw new Error("Date de relance requise");
      return {
        status: "relance",
        nextFollowUpAt: opts.followUpAt,
        nextAction: opts.followUpTime ? `Relance à ${hourLabel(opts.followUpTime)}` : "Relance après appel",
        ...(hasEvent ? { eventOutcome: "attended" as const, eventIssue: "relance" as const } : {}),
      };
    }
    case "lost":
      return {
        status: "closed_lost",
        nextFollowUpAt: null,
        nextAction: null,
        lossReason: clean(opts.lossReason),
        ...(hasEvent ? { eventOutcome: "attended" as const, eventIssue: "lost" as const } : {}),
      };
    case "present": {
      const outcome = hasEvent ? { eventOutcome: "attended" as const } : {};
      if (opts.touchesProspect === false) return outcome;
      if (opts.hasUpcomingFollowUp === true && opts.currentNextAction !== "Rappeler") return outcome;
      return {
        nextFollowUpAt: addDaysIso(today, 3),
        nextAction: "Relance après appel",
        ...outcome,
      };
    }
    case "no_show": {
      // Un absent n'a pas d'issue d'appel : on efface celle qui aurait été notée par erreur.
      const outcome = hasEvent ? { eventOutcome: "no_show" as const, eventIssue: null } : {};
      if (opts.touchesProspect === false) return outcome;
      return {
        nextFollowUpAt: opts.followUpAt ?? today,
        nextAction: "Rappeler",
        ...outcome,
      };
    }
  }
}

function frenchDay(iso: string): string {
  const m = iso.match(/^\d{4}-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[1]}` : iso;
}

/** Texte de l'interaction `rdv` : « Issue : À relancer (Prix) le 26/10, note ». Sans tiret cadratin. */
export function outcomeSummary(choice: OutcomeChoice, opts: OutcomeOpts = {}): string {
  const note = clean(opts.note);
  let head: string;
  switch (choice) {
    case "closed_pc":
      head = "Closé Power Coaching";
      break;
    case "closed_acc":
      head = "Closé Accélérateur";
      break;
    case "relance": {
      const blocker = clean(opts.blocker);
      head = `À relancer${blocker ? ` (${blocker})` : ""}${
        opts.followUpAt ? ` le ${frenchDay(opts.followUpAt)}` : ""
      }${opts.followUpTime ? ` à ${hourLabel(opts.followUpTime)}` : ""}`;
      break;
    }
    case "lost": {
      const reason = clean(opts.lossReason);
      head = `Perdu${reason ? ` (${reason})` : ""}`;
      break;
    }
    case "no_show":
      head = "Absent";
      break;
    case "present":
      head = "Présent";
      break;
  }
  if ((choice === "present" || choice === "no_show") && opts.touchesProspect === false && opts.eventDay) {
    head += ` (RDV du ${frenchDay(opts.eventDay)})`;
  }
  return `${OUTCOME_BODY_PREFIX} ${head}${note ? `, ${note}` : ""}`;
}

/** Préfixe du texte de l'interaction `rdv` écrite par l'issue (voir `outcomeSummary`). */
export const OUTCOME_BODY_PREFIX = "Issue :";

const USER_KINDS = new Set(["note", "appel", "message", "email", "relance"]);

/**
 * Vrai si Christophe a travaillé la fiche : interaction saisie (hors `rdv` créées par la synchro),
 * issue notée sur un RDV, ou interaction `rdv` écrite par une issue. Sert à ne pas archiver
 * (closed_lost) une fiche dont un RDV a été annulé ou déplacé.
 */
export function hasUserActivity(input: {
  interactions: { kind: string; body: string }[];
  eventOutcomes: (string | null)[];
}): boolean {
  if (input.eventOutcomes.some((o) => o !== null)) return true;
  return input.interactions.some(
    (i) => USER_KINDS.has(i.kind) || (i.kind === "rdv" && i.body.startsWith(OUTCOME_BODY_PREFIX)),
  );
}

const AUTO_ARCHIVE_STATUSES = new Set<ProspectStatus>(["a_contacter", "en_discussion", "relance"]);

/**
 * RDV annulé : archiver (closed_lost) seulement une fiche ouverte, sans activité ni vente, et
 * sans autre RDV confirmé à venir pour le même e-mail (annulé puis reprogrammé).
 */
export function shouldAutoArchiveOnCancel(input: {
  status: ProspectStatus;
  userActivity: boolean;
  hasSales: boolean;
  hasUpcomingConfirmed: boolean;
}): boolean {
  return (
    AUTO_ARCHIVE_STATUSES.has(input.status) &&
    !input.userActivity &&
    !input.hasSales &&
    !input.hasUpcomingConfirmed
  );
}

export const ALREADY_BOUGHT_ERROR =
  "Ce prospect a déjà acheté : change son statut depuis sa fiche si besoin.";

/**
 * Message de refus d'une issue pour ce statut, ou `null` si elle est permise. `force` : le client
 * a confirmé qu'il change l'issue d'un prospect déjà gagné (la vente reste enregistrée).
 */
export function outcomeRefusedFor(
  status: ProspectStatus,
  choice: OutcomeChoice,
  force?: boolean,
): string | null {
  if (force === true) return null;
  if (
    status === "closed_won" &&
    choice !== "closed_pc" &&
    choice !== "closed_acc" &&
    choice !== "present" &&
    choice !== "no_show"
  ) {
    return ALREADY_BOUGHT_ERROR;
  }
  return null;
}

/**
 * Pointer ce RDV doit-il toucher la fiche (relance, prochaine action) ? Vrai si c'est le RDV
 * `confirmed` commencé (début <= now) le plus récent de la fiche ET que son jour de Paris est
 * au plus 7 jours avant aujourd'hui. Sinon seuls l'issue du RDV et l'historique s'écrivent.
 */
export function pointageTouchesProspect(input: {
  eventId: number;
  events: { id: number; startTime: string; status: string }[];
  now: Date;
}): boolean {
  let latest: { id: number; t: number; startTime: string } | null = null;
  for (const e of input.events) {
    if (e.status !== "confirmed") continue;
    const t = new Date(e.startTime).getTime();
    if (Number.isNaN(t) || t > input.now.getTime()) continue;
    if (!latest || t > latest.t) latest = { id: e.id, t, startTime: e.startTime };
  }
  if (!latest || latest.id !== input.eventId) return false;
  const eventDay = parisDateOf(latest.startTime);
  const today = parisDateOf(input.now.toISOString());
  const limit = today ? addDaysIso(today, -7) : null;
  return eventDay !== null && limit !== null && eventDay >= limit;
}
